import { Box, Flex, Text, Image, Button } from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import LabCorpLogo from 'assets/images/LabCorpLogo.png'
import { useLabSchedulingContext } from 'context/LabSchedulingContext/LabSchedulingProvider'
import { useLabScheduling } from 'hooks/useLabScheduling/useLabscheduling'
import { Amplitude } from 'utils/amplitude.utils'
import { LabScheduleDoneProps, LabProvider } from 'utils/amplitude.types'
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { useLabSchedulingError } from 'hooks/useLabScheduling/useLabSchedulingError'
import { RoutePaths } from 'containers/Core/Routes'

const ConfrmLabAppointment = () => {
  const {
    setCurrentScreen,
    selectedLab,
    labState,
    setLabAppointmentState,
    labAppointmentState,
    isModifyFlow,
    amplitudeLabSchedulingMetadata,
  } = useLabSchedulingContext()
  const { handleLabSchedulingError } = useLabSchedulingError()
  const { useScheduleAppointment, useModifyAppointment } = useLabScheduling()
  const {
    mutateAsync: scheduleAppointment,
    isLoading: isLoadingScheduleAppointment,
  } = useScheduleAppointment()
  const {
    mutateAsync: modifyAppointment,
    isLoading: isLoadingModifyAppointment,
  } = useModifyAppointment()
  const {
    selectedLabId,
    selectedLabAddress,
    selectedDate,
    selectedTime,
    selectedAddress,
    displayTimezone,
    labTimezone,
  } = labState

  const dateTime = `${selectedDate}T${selectedTime}`

  const timeInLabTimezone = zonedTimeToUtc(dateTime, labTimezone)
  const timeInDisplayTimezone = utcToZonedTime(
    timeInLabTimezone,
    displayTimezone
  )

  const formattedDate = format(
    timeInDisplayTimezone,
    "EEEE, MMMM d, yyyy 'at' h:mm aaaa zzz",
    {
      timeZone: displayTimezone,
    }
  )

  const editHandler = () => {
    setCurrentScreen('timeslotMap')
  }

  const submitHandler = async () => {
    try {
      const appointmentData = {
        selectedLab,
        locationId: selectedLabId,
        appointmentTime: dateTime,
        selectedAddress,
      }

      const lab =
        selectedLab === 'LabCorp' ? LabProvider.LABCORP : LabProvider.QUEST

      const amplitudeData: LabScheduleDoneProps = {
        lab_location: selectedLabAddress,
        lab_code: selectedLabId,
        is_reschedule: isModifyFlow,
        source: amplitudeLabSchedulingMetadata.source,
        lab_appointment_time:
          amplitudeLabSchedulingMetadata.lab_appointment_time,
        lab_provider: lab,
        days_since_nearest_slot:
          amplitudeLabSchedulingMetadata.days_since_nearest_slot,
        patient_action_id:
          amplitudeLabSchedulingMetadata.patient_action_id ?? '',
        lab_appointment_id:
          amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
      }

      Amplitude.labScheduleDone(amplitudeData)

      let labAppointmentId = ''

      if (isModifyFlow) {
        if (!labAppointmentState.confirmationNumber) {
          console.error('No confirmation number')
          return
        }

        const modifyAppointmentData = {
          ...appointmentData,
          confirmationNumber: labAppointmentState.confirmationNumber,
        }

        const result = await modifyAppointment(modifyAppointmentData)

        labAppointmentId = result.id

        setLabAppointmentState(result)
      } else {
        const result = await scheduleAppointment(appointmentData)

        labAppointmentId = result.id

        setLabAppointmentState(result)
      }

      if (labAppointmentId) {
        window.location.href = `${RoutePaths.LAB_SCHEDULING}?labAppointmentId=${labAppointmentId}`
      } else {
        setCurrentScreen('confirmation')
      }
    } catch (error) {
      handleLabSchedulingError(error, 'Failed to book appointment')
    }
  }

  return (
    <Box bg="white" p={8} borderRadius="lg" boxShadow="lg" width="sm">
      <Flex flexDirection="column" alignItems="center" justifyContent="center">
        <Text fontFamily="Instrument Serif" fontSize="1.5rem">
          Confirm your appointment details
        </Text>

        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          marginBottom="2rem"
        >
          <Text fontWeight="500" color="#161E35" marginBottom=".5rem">
            Lab Location
          </Text>
          <Flex width="100%" justifyContent="space-between">
            <Text margin="0">{selectedLabAddress}</Text>
            <Button
              variant="link"
              onClick={() => editHandler()}
              color="#4678E6"
              fontWeight="400"
            >
              Edit
            </Button>
          </Flex>
        </Flex>

        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          marginBottom="2rem"
        >
          <Text fontWeight="500" color="#161E35" marginBottom=".5rem">
            Date and time
          </Text>
          <Flex width="100%" justifyContent="space-between">
            <Text margin="0">{formattedDate}</Text>
            <Button
              variant="link"
              onClick={() => editHandler()}
              color="#4678E6"
              fontWeight="400"
            >
              Edit
            </Button>
          </Flex>
        </Flex>

        <ChakraPrimaryButton
          width="100%"
          onClick={() => submitHandler()}
          isLoading={isLoadingScheduleAppointment || isLoadingModifyAppointment}
        >
          Book appointment
        </ChakraPrimaryButton>
        {selectedLab === 'LabCorp' && (
          <Image src={LabCorpLogo} width="90px" marginTop="2.5rem" />
        )}
      </Flex>
    </Box>
  )
}

export default ConfrmLabAppointment
